import {useState, useEffect} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Switch} from '@/components/ui/switch'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {Combobox, ComboboxOption} from '@/components/ui/combobox'
import {Textarea} from '@/components/ui/textarea'
import {OwnersPicker, areOwnersValid} from '@/components/shared/OwnersPicker'
import {AccessProfileCreateRequestFields, NAME_ATTRIBUTE_CODE} from '@/types/access-profile'
import type {AccessProfile, AccessProfileCreateRequest, AccessProfileUpdateRequest} from '@/types/access-profile'
import type {InitialOwnerRequest} from '@/types/owner'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useAppConfig} from '@/hooks/use-app-config'


interface AccessProfileFormProps {
    profile?: AccessProfile
    onSubmit: (data: AccessProfileCreateRequest | AccessProfileUpdateRequest) => Promise<void>
    onCancel: () => void
    isLoading?: boolean
}

export function AccessProfileForm({
    profile, onSubmit, onCancel, isLoading}: AccessProfileFormProps) {
    const services = useServices()
    const isEdit = !!profile
    const i18n = useI18n()
    const {riskLevelTypes: riskLevels, accessProfileResponsibilityTypes} = useAppConfig()

    // Form state
    const [resourceId, setResourceId] = useState(profile?.resource.id || '')
    const [environment, setEnvironment] = useState(profile?.environment || '')
    const [code, setCode] = useState(profile?.code || '')
    const [codeManuallyEdited, setCodeManuallyEdited] = useState(isEdit)
    const [name, setName] = useState<string>(profile?.name || '')
    const [description, setDescription] = useState<string>(profile?.description || '')
    const [riskLevel, setRiskLevel] = useState(profile?.riskLevel || '')
    const [reviewFrequencyDays, setReviewFrequencyDays] = useState<number | ''>(profile?.reviewFrequencyDays || '')
    const [requestable, setRequestable] = useState(profile?.requestable ?? false)
    const [autoAssignable, setAutoAssignable] = useState(profile?.autoAssignable ?? false)
    const [expiryRequired, setExpiryRequired] = useState(profile?.expiryRequired ?? false)
    const [maxDurationDays, setMaxDurationDays] = useState<number | ''>(profile?.maxDurationDays || '')

    const defaultOwnerResponsibility =
        accessProfileResponsibilityTypes.find((o) => o.code === 'OWNER')?.code
        ?? accessProfileResponsibilityTypes[0]?.code
        ?? ''

    const [pendingOwners, setPendingOwners] = useState<InitialOwnerRequest[]>([])
    const [ownerLabels, setOwnerLabels] = useState<Record<string, string>>({})

    useEffect(() => {
        if (!isEdit && pendingOwners.length === 0 && defaultOwnerResponsibility) {
            setPendingOwners([{ownerPartyId: '', responsibility: defaultOwnerResponsibility}])
        }
    }, [isEdit, defaultOwnerResponsibility, pendingOwners.length])

    const suggestCode = (newName: string) => {
        return newName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 100)
    }

    const handleNameChange = (newName: string) => {
        setName(newName)
        if (!codeManuallyEdited && !isEdit) {
            setCode(suggestCode(newName))
        }
    }

    // Resource search for combobox
    const [resourceSearchQuery, setResourceSearchQuery] = useState('')
    const {data: resourceSearchData, isLoading: resourcesLoading} = useQuery({
        queryKey: ['resources', 'search', resourceSearchQuery],
         
        queryFn: () => services.resources.list({q: resourceSearchQuery, size: 10}),
    })
    const resourceOptions: ComboboxOption[] = (resourceSearchData?.content || []).map((r) => ({
        value: r.id,
        label: r.derivedDisplayName,
        description: r.nativeKey,
    }))

    // Fetch selected resource by ID (for display and environment derivation)
    const {data: selectedResource} = useQuery({
        queryKey: ['resources', resourceId],
        queryFn: () => services.resources.detail(resourceId),
        enabled: !!resourceId,
    })

    // Derive environment from selected resource (for create mode)
    useEffect(() => {
        if (!isEdit) {
            if (selectedResource?.targetSystem.subType) {
                setEnvironment(selectedResource.targetSystem.subType)
            } else if (!resourceId) {
                // Clear environment when resource is cleared
                setEnvironment('')
            }
        }
    }, [isEdit, selectedResource, resourceId])

    // Update maxDurationDays validation when expiryRequired changes
    useEffect(() => {
        if (!expiryRequired) {
            setMaxDurationDays('')
        }
    }, [expiryRequired])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const descriptionValue = description.trim() === '' ? undefined : description

        if (isEdit) {
            const updateData: AccessProfileUpdateRequest = {
                description: descriptionValue,
                riskLevel,
                reviewFrequencyDays: Number(reviewFrequencyDays),
                requestable,
                autoAssignable,
                expiryRequired,
                maxDurationDays: maxDurationDays ? Number(maxDurationDays) : undefined,
                version: profile.version,
            }
            await onSubmit(updateData)
        } else {
            const createData: AccessProfileCreateRequest = {
                resourceId,
                environment,
                code,
                attributes: [{attributeName: NAME_ATTRIBUTE_CODE, valueString: name}],
                description: descriptionValue,
                riskLevel,
                reviewFrequencyDays: Number(reviewFrequencyDays),
                requestable,
                autoAssignable,
                expiryRequired,
                maxDurationDays: maxDurationDays ? Number(maxDurationDays) : undefined,
                owners: pendingOwners,
            }
            await onSubmit(createData)
        }
    }

    const createOwnersValid = isEdit || areOwnersValid(pendingOwners)
    const isValid = name.trim() !== '' && riskLevel && reviewFrequencyDays &&
        (isEdit || (resourceId && environment && code)) &&
        (!expiryRequired || maxDurationDays) &&
        createOwnersValid

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Resource (only for create) */}
            {!isEdit && (
                <div className="space-y-2">
                    <Label htmlFor="resource">{i18n.accessProfiles.resource} *</Label>
                    <Combobox
                        options={resourceOptions}
                        value={resourceId || null}
                        onValueChange={(v) => setResourceId(v || '')}
                        onSearch={setResourceSearchQuery}
                        placeholder={i18n.accessProfiles.searchResource}
                        emptyMessage={i18n.accessProfiles.noResourcesFound}
                        isLoading={resourcesLoading}
                        className="w-full"
                    />
                </div>
            )}

            {/* Resource display (only for edit) */}
            {isEdit && (
                <div className="space-y-2">
                    <Label>{i18n.accessProfiles.resource}</Label>
                    <p className="text-sm text-muted-foreground">
                        {selectedResource?.derivedDisplayName || profile.resource.displayName}
                    </p>
                </div>
            )}

            {/* Environment (derived from resource, read-only) */}
            <div className="space-y-2">
                <Label>{i18n.accessProfiles.environment}</Label>
                {environment ? (
                    <p className="text-sm text-muted-foreground">{environment}</p>
                ) : (
                    <p className="text-sm text-muted-foreground italic">{i18n.accessProfiles.selectResourceEnv}</p>
                )}
            </div>

            {/* Name (create only — display name is derived from attributes on update). */}
            {!isEdit && (
                <div className="space-y-2">
                    <Label htmlFor="name">{i18n.common.name} *</Label>
                    <Input
                        id="name"
                        value={name}
                        onChange={(e) => handleNameChange(e.target.value)}
                        placeholder={i18n.accessProfiles.namePlaceholder}
                        required
                    />
                </div>
            )}

            {/* Code */}
            <div className="space-y-2">
                <Label htmlFor={AccessProfileCreateRequestFields.code}>{i18n.accessProfiles.code} *</Label>
                <Input
                    id={AccessProfileCreateRequestFields.code}
                    value={code}
                    onChange={(e) => {
                        setCode(e.target.value)
                        if (!isEdit) setCodeManuallyEdited(true)
                    }}
                    placeholder={i18n.accessProfiles.codePlaceholder}
                    required
                    disabled={isEdit}
                    // eslint-disable-next-line i18next/no-literal-string
                    pattern="^[a-z][a-z0-9-]{2,99}$"
                />
                <p className="text-xs text-muted-foreground">{i18n.accessProfiles.codeDesc}</p>
            </div>

            {/* Description */}
            <div className="space-y-2">
                <Label htmlFor={AccessProfileCreateRequestFields.description}>{i18n.common.description}</Label>
                <Textarea
                    id={AccessProfileCreateRequestFields.description}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={i18n.accessProfiles.descriptionPlaceholder}
                    rows={3}
                />
            </div>

            {/* Owners — multi-owner picker on create. Edit-mode owner changes go through OwnersSection on the detail page. */}
            {!isEdit && (
                <div className="space-y-2">
                    <Label>{i18n.accessProfiles.owner}</Label>
                    <OwnersPicker
                        owners={pendingOwners}
                        onOwnersChange={setPendingOwners}
                        responsibilityTypes={accessProfileResponsibilityTypes}
                        ownerLabels={ownerLabels}
                        onOwnerLabelsChange={setOwnerLabels}
                    />
                    <p className="text-xs text-muted-foreground">{i18n.accessProfiles.ownerPartyDesc}</p>
                </div>
            )}

            {/* Risk Level */}
            <div className="space-y-2">
                <Label htmlFor="riskLevel">{i18n.accessProfiles.riskLevel} *</Label>
                <Select value={riskLevel} onValueChange={setRiskLevel}>
                    <SelectTrigger data-testid="select-risk-level">
                        <SelectValue placeholder={i18n.accessProfiles.selectRiskLevel}/>
                    </SelectTrigger>
                    <SelectContent>
                        {riskLevels.map((level) => (
                            <SelectItem key={level.code} value={level.code}>
                                {level.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Review Frequency */}
            <div className="space-y-2">
                <Label htmlFor="reviewFrequencyDays">{i18n.accessProfiles.reviewFrequencyDays} *</Label>
                <Select
                    value={reviewFrequencyDays ? String(reviewFrequencyDays) : ''}
                    onValueChange={(v) => setReviewFrequencyDays(Number(v))}
                >
                    <SelectTrigger data-testid="select-review-frequency">
                        <SelectValue placeholder={i18n.accessProfiles.selectReviewFrequency}/>
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="30">{i18n.accessProfiles.reviewFrequencyMonthly}</SelectItem>
                        <SelectItem value="60">{i18n.accessProfiles.reviewFrequencyBimonthly}</SelectItem>
                        <SelectItem value="90">{i18n.accessProfiles.reviewFrequencyQuarterly}</SelectItem>
                        <SelectItem value="180">{i18n.accessProfiles.reviewFrequencySemiAnnual}</SelectItem>
                        <SelectItem value="365">{i18n.accessProfiles.reviewFrequencyAnnual}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Boolean switches */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <Label htmlFor={AccessProfileCreateRequestFields.requestable}>{i18n.accessProfiles.requestable}</Label>
                        <p className="text-sm text-muted-foreground">{i18n.accessProfiles.requestableDesc}</p>
                    </div>
                    <Switch
                        id={AccessProfileCreateRequestFields.requestable}
                        checked={requestable}
                        onCheckedChange={setRequestable}
                    />
                </div>
                <div className="flex items-center justify-between">
                    <div>
                        <Label htmlFor={AccessProfileCreateRequestFields.autoAssignable}>{i18n.accessProfiles.autoAssignable}</Label>
                        <p className="text-sm text-muted-foreground">{i18n.accessProfiles.autoAssignableDesc}</p>
                    </div>
                    <Switch
                        id={AccessProfileCreateRequestFields.autoAssignable}
                        checked={autoAssignable}
                        onCheckedChange={setAutoAssignable}
                    />
                </div>
                <div className="flex items-center justify-between">
                    <div>
                        <Label htmlFor={AccessProfileCreateRequestFields.expiryRequired}>{i18n.accessProfiles.expiryRequired}</Label>
                        <p className="text-sm text-muted-foreground">{i18n.accessProfiles.expiryRequiredDesc}</p>
                    </div>
                    <Switch
                        id={AccessProfileCreateRequestFields.expiryRequired}
                        checked={expiryRequired}
                        onCheckedChange={setExpiryRequired}
                    />
                </div>
            </div>

            {/* Max Duration (only when expiry required) */}
            {expiryRequired && (
                <div className="space-y-2">
                    <Label htmlFor={AccessProfileCreateRequestFields.maxDurationDays}>{i18n.accessProfiles.maxDurationLabel}</Label>
                    <Input
                        id={AccessProfileCreateRequestFields.maxDurationDays}
                        type="number"
                        min="1"
                        value={maxDurationDays}
                        onChange={(e) => setMaxDurationDays(e.target.value ? Number(e.target.value) : '')}
                        placeholder={i18n.accessProfiles.maxDurationPlaceholder}
                        required={expiryRequired}
                    />
                </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading} data-testid="btn-cancel">
                    {i18n.common.cancel}
                </Button>
                <Button type="submit" disabled={isLoading || !isValid} data-testid="btn-submit">
                    {isLoading ? i18n.common.saving : (isEdit ? i18n.common.saveChanges : i18n.accessProfiles.createProfile)}
                </Button>
            </div>
        </form>
    )
}
